import type { State } from '@src/redux/configureStore'
import { defaultState } from '@src/redux/reducers/objects/details/default.state'
import {
  formatSearchResultByField,
  InfoDetailsResult,
  parseInfoDetails
} from '@src/utils/resource.utils'
import { View, mapDataToGraph } from '@src/utils/graph.helpers'
import { ResourceTypeEnum } from '@src/endpoints/services/commonHdr.service'
import type {
  ObjectDetailInfo,
  ObjectServiceQueryReq,
  ObjectRollUpGraphItem
} from '@src/endpoints/services/graphQueryMgr.service'
import {
  GraphQueryMgr,
  DataType,
  ObjectTileType
} from '@src/endpoints/services/graphQueryMgr.service'
import type { SearchResult } from '@src/redux/reducers/autocomplete.query'
import * as ActionCreators from './action.creators'
import { getInfo } from '@src/redux/reducers/objects/helpers/object.info.mapper'
import { defaultHandleStatus } from '@src/endpoints/helpers'
import { forEach } from 'lodash'
import { CloudTypeEnum } from '@src/endpoints/services/base.service'

const servicesTile = 'services'
const ObjectTileByTile = {
  users: {
    tile: ObjectTileType.OBJECT_TILE_ACCESS,
    identity_type: ResourceTypeEnum.USER
  },
  admin: {
    tile: ObjectTileType.OBJECT_TILE_ACCESS,
    identity_type: 'ADMIN'
  },
  group: {
    tile: ObjectTileType.OBJECT_TILE_MEMBERSHIP,
    identity_type: ResourceTypeEnum.GROUP
  },
  role: {
    tile: ObjectTileType.OBJECT_TILE_MEMBERSHIP,
    identity_type: ResourceTypeEnum.ROLE
  },
  services: {
    tile: ObjectTileType.OBJECT_TILE_IDENTITY,
    identity_type: ResourceTypeEnum.INSTANCE
  }
}
const instanceTypeByCloud = {
  [CloudTypeEnum.AWS]: ResourceTypeEnum.AWS_INSTANCE,
  [CloudTypeEnum.GCP]: ResourceTypeEnum.GCP_VM_INSTANCE,
  [CloudTypeEnum.AZURE]: ResourceTypeEnum.AZURE_VIRTUAL_MACHINE,
  [CloudTypeEnum.IDP_AZURE_AD]: ResourceTypeEnum.AZURE_VIRTUAL_MACHINE,
  [CloudTypeEnum.IDP_OKTA]: ResourceTypeEnum.AWS_INSTANCE
}

interface GetObjectDetailsOptions {
  objectName: string
  objectType: ResourceTypeEnum
  accountId?: string
  queryBy?: string
  selectedGraphView?: View
  usageBy?: number
  selectedTile?: string
  useApiV2?: boolean
  tile?: string
  identity_type?: string
  cloudType?: CloudTypeEnum
  callback?: () => void
}

export function cleaner() {
  return (dispatch: any, getState: () => State) => {
    dispatch(ActionCreators.disposeDetails())
  }
}

export function serverGraphErrors() {
  return (dispatch: any, getState: () => State) => {
    dispatch(ActionCreators.detailsErrors({ isPendingGraph: false }))
  }
}

function objectDetailsErrors(errors?: string) {
  return (dispatch: any, getState: () => State) => {
    dispatch(
      ActionCreators.detailsErrors({
        isPendingGraph: false
      })
    )
  }
}

export function getDetails(
  objectName: string,
  objectType: ResourceTypeEnum,
  accountId: string,
  tile?: ObjectTileType,
  identity_type?: string
) {
  return (dispatch: any, getState: () => State) => {
    dispatch(getObjectDetailsInfo({ objectName, objectType, accountId }))
    dispatch(
      getObjectDetailsGraph({
        objectName,
        objectType,
        accountId,
        tile,
        identity_type
      })
    )
    dispatch(
      getObjectDetailsTable({
        objectName,
        objectType,
        accountId,
        tile,
        identity_type
      })
    )
  }
}

export function getObjectDetailsInfo({
  objectName,
  objectType,
  accountId
}: GetObjectDetailsOptions) {
  return (dispatch: any, getState: () => State) => {
    const params = {
      uri: objectName,
      type: objectType,
      account_id: accountId,
      data_type: DataType.INFO
    } as ObjectServiceQueryReq

    dispatch(
      ActionCreators.changeDetailsState({
        isPending: true,
        selectedTile: defaultState.selectedTile
      })
    )

    GraphQueryMgr.ObjectServiceQuery({ params })
      .then(res =>
        defaultHandleStatus(res, objectDetailsErrors, true, dispatch)
      )
      .then(res => {
        dispatch(
          ActionCreators.changeDetailsState({
            selectedTile: defaultState.selectedTile
          })
        )
        const { object_detail_info = {} as ObjectDetailInfo } = res
        const { valueMap } = object_detail_info
        const objectInfo = getInfo(valueMap)
        const humanNamesResult: InfoDetailsResult = parseInfoDetails(objectInfo)
        dispatch(
          ActionCreators.getDetailsStatsData(
            res.object_detail_info,
            humanNamesResult
          )
        )
      })
      .catch((res: Response) => dispatch(objectDetailsErrors(res?.statusText)))
  }
}

export function getObjectDetailsGraph({
  objectName,
  objectType,
  accountId,
  tile = ObjectTileType.OBJECT_TILE_ACCESS,
  identity_type
}: GetObjectDetailsOptions) {
  return (dispatch: any, getState: () => State) => {
    const params = {
      uri: objectName,
      type: objectType,
      account_id: accountId,
      data_type: DataType.ROLL_UP_GRAPH,
      tile,
      identity_type
    } as ObjectServiceQueryReq
    if (!identity_type) delete params.identity_type
    dispatch(requestPendingGraph(true))
    GraphQueryMgr.ObjectServiceQuery({ params })
      .then(res =>
        defaultHandleStatus(res, objectDetailsErrors, true, dispatch)
      )
      .then(res => {
        const { shell } = getState()
        const { resourceTypes } = shell
        const { object_detail_info = {} as ObjectDetailInfo } = res
        const { roll_up_graph = [] as ObjectRollUpGraphItem[] } =
          object_detail_info
        const graphParsed = mapDataToGraph({
          data: roll_up_graph,
          resourceTypes,
          options: {
            mainIdKey: 'resource_uri',
            mainNameKey: 'resource',
            typeKey: 'identity_type',
            countKey: 'identity_count'
          }
        })
        const graph = graphParsed
        dispatch(ActionCreators.getGraphSuccess(graph))
      })
      .catch((res: Response) => dispatch(objectDetailsErrors(res?.statusText)))
  }
}

export function getObjectDetailsTable({
  objectName,
  objectType,
  accountId,
  tile = ObjectTileType.OBJECT_TILE_ACCESS,
  identity_type
}: GetObjectDetailsOptions) {
  return (dispatch: any, getState: () => State) => {
    const params = {
      uri: objectName,
      type: objectType,
      account_id: accountId,
      data_type: DataType.TABLE_DATA,
      tile,
      identity_type
    } as ObjectServiceQueryReq
    if (!identity_type) delete params.identity_type

    dispatch(
      ActionCreators.changeDetailsState({
        isPending: true,
        selectedTile: defaultState.selectedTile
      })
    )
    GraphQueryMgr.ObjectServiceQuery({ params })
      .then(res =>
        defaultHandleStatus(res, objectDetailsErrors, true, dispatch)
      )
      .then(res => {
        const { auth } = getState()
        const { tenant } = auth
        const { account_info = [] } = tenant || {}
        const { object_detail_info = {} as ObjectDetailInfo } = res
        const { result_table } = object_detail_info as any
        let formattedTable: SearchResult[] = result_table || []
        formattedTable = formatSearchResultByField(
          formattedTable,
          'identity_uri',
          account_info
        )
        let selectedTile: string
        forEach(ObjectTileByTile, (v, k) => {
          if (
            v.tile === tile &&
            (v.identity_type === identity_type ||
              Object.values(instanceTypeByCloud).includes(
                identity_type as ResourceTypeEnum
              ))
          ) {
            selectedTile = k
          }
        })
        dispatch(ActionCreators.getTableSuccess(formattedTable, selectedTile))
      })
      .catch((res: Response) => dispatch(objectDetailsErrors(res?.statusText)))
  }
}

export function requestPendingGraph(isPending: boolean) {
  return (dispatch: any, getState: () => State) => {
    dispatch(ActionCreators.requestPendingGraph(isPending))
  }
}

export function startGetGraph() {
  return (dispatch: any, getState: () => State) => {
    dispatch(ActionCreators.requestPendingGraph(true))
  }
}

export function getObjectDetailsForTile({
  objectName,
  objectType,
  accountId,
  selectedTile = null,
  cloudType,
  callback
}: GetObjectDetailsOptions) {
  return (dispatch: any, getState: () => State) => {
    dispatch(
      ActionCreators.changeDetailsState({
        selectedTile,
        selectedGraphView: View.access,
        graph: defaultState.graph,
        table: defaultState.table,
        isPending: true
      })
    )
    const { tile, identity_type } = ObjectTileByTile[selectedTile] || {}
    const identityType =
      selectedTile === servicesTile
        ? instanceTypeByCloud[cloudType]
        : identity_type
    dispatch(
      getObjectDetailsGraph({
        objectName,
        objectType,
        accountId,
        tile,
        identity_type: identityType
      })
    )
    dispatch(
      getObjectDetailsTable({
        objectName,
        objectType,
        accountId,
        tile,
        identity_type: identityType
      })
    )
    if (callback) callback()
  }
}
